// src/pages/Login.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Visibility, VisibilityOff, LockOutlined, EmailOutlined } from '@mui/icons-material';
import LoginBackground from '../components/LoginBackground';
import { useLogin } from '../hooks/useLogin';
import { useNombreClinica } from '../hooks/useNombreClinica';

// 🧩 Subcomponente puramente visual (Presentacional): icono Klyndu dentro del
// anillo que se va completando al escribir la contraseña
const KlynduLogo = ({ passwordLen }) => {
    const isComplete = passwordLen >= 8;
    const progress = Math.min((passwordLen / 8) * 100, 100);

    const mainColor = isComplete ? '#10b981' : '#0ea5e9';
    const strokeDashoffset = 251.2 - (251.2 * progress) / 100;

    return (
        <div className="relative flex justify-center mb-6 drop-shadow-xl">
            <svg viewBox="0 0 100 100" className="w-24 h-24" style={{ filter: `drop-shadow(0 0 12px ${mainColor}40)` }}>
                <circle cx="50" cy="50" r="40" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.05)" strokeWidth="2" />
                <circle cx="50" cy="50" r="40" fill="none" stroke={mainColor} strokeWidth="2.5" strokeDasharray="251.2" strokeDashoffset={strokeDashoffset} className="transition-all duration-500 ease-out" />
                <image href="/brand/klyndu-icono.svg" x="27" y="27" width="46" height="46" />
            </svg>
        </div>
    );
};

// 🎼 Orquestador Principal
export default function Login() {
    // 1. IMPORTAMOS LA LÓGICA DE DOMINIO (SRP)
    const {
        email, setEmail,
        password, setPassword,
        token2FA, setToken2FA,
        showPassword, setShowPassword,
        requires2FA, loading, error,
        handleSubmit
    } = useLogin();
    const nombreClinica = useNombreClinica();

    // 2. RENDERIZADO VISUAL
    return (
        <LoginBackground passwordLen={password.length}>
            <div className="w-full max-w-[420px] px-4 mx-auto z-10">
                {/* TARJETA GLASSMORPHISM ENTERPRISE */}
                <div className="relative p-8 md:p-10 rounded-[2.5rem] bg-[#0a0f1c]/70 backdrop-blur-2xl border border-white/[0.05] shadow-[0_0_80px_rgba(0,0,0,0.6)] overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                    <div className="flex flex-col items-center mb-8">
                        <KlynduLogo passwordLen={password.length} />
                        <img src="/brand/klyndu-nombre-blanco.svg" alt="Klyndu" className="h-8 w-auto mb-5" />
                        <h1 className="text-2xl font-black text-white tracking-tight text-center">
                            {requires2FA ? 'Verificación en dos pasos' : 'Accede a tu clínica'}
                        </h1>
                        {nombreClinica && (
                            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1 text-center">
                                {nombreClinica}
                            </p>
                        )}
                    </div>

                    {error && (
                        <div className="p-3 mb-6 text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl text-center flex items-center justify-center gap-2 animate-fade-in">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!requires2FA ? (
                            <>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-500 group-focus-within:text-sky-400 transition-colors duration-300">
                                        <EmailOutlined fontSize="small" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        placeholder="Correo electrónico"
                                        aria-label="Correo electrónico"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full py-3.5 pl-12 pr-4 bg-[#060b14]/50 border border-white/10 rounded-2xl text-white placeholder-slate-600 text-sm font-medium focus:outline-none focus:border-sky-500/50 focus:ring-4 focus:ring-sky-500/10 transition-all shadow-inner"
                                    />
                                </div>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-500 group-focus-within:text-sky-400 transition-colors duration-300">
                                        <LockOutlined fontSize="small" />
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        required
                                        placeholder="Contraseña"
                                        aria-label="Contraseña"
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full py-3.5 pl-12 pr-12 bg-[#060b14]/50 border border-white/10 rounded-2xl text-white placeholder-slate-600 text-sm font-medium focus:outline-none focus:border-sky-500/50 focus:ring-4 focus:ring-sky-500/10 transition-all shadow-inner"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-white transition-colors"
                                    >
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-4 animate-fade-in">
                                <p className="text-xs text-center text-slate-400 font-medium">
                                    Escribe el código de 6 cifras que muestra tu aplicación de autenticación.
                                </p>
                                <input
                                    type="text"
                                    name="token2FA"
                                    required
                                    maxLength={6}
                                    value={token2FA}
                                    onChange={(e) => setToken2FA(e.target.value.replace(/\D/g, ''))}
                                    placeholder="000 000"
                                    className="w-full py-4 bg-[#060b14]/50 border border-white/10 rounded-2xl text-white text-center text-2xl font-black tracking-[0.3em] placeholder-slate-700 focus:outline-none focus:border-sky-500/50 focus:ring-4 focus:ring-sky-500/10 transition-all shadow-inner"
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-4 mt-2 rounded-2xl font-black text-xs uppercase tracking-widest transition-all duration-300 ${
                                (email.trim() && password) || requires2FA
                                    ? 'bg-sky-500 text-white hover:bg-sky-400 shadow-[0_0_20px_rgba(14,165,233,0.3)] hover:shadow-[0_0_30px_rgba(14,165,233,0.5)] transform hover:-translate-y-0.5'
                                    : 'bg-white/5 text-white/30 border border-white/10 cursor-not-allowed'
                            }`}
                        >
                            {loading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    <span>Entrando…</span>
                                </div>
                            ) : requires2FA ? 'Verificar código' : 'Iniciar sesión'}
                        </button>
                    </form>

                    {!requires2FA && (
                        <div className="mt-5 text-center">
                            <Link to="/recuperar-contrasena" className="text-xs font-bold text-slate-400 hover:text-sky-300 transition-colors">
                                ¿Has olvidado tu contraseña?
                            </Link>
                        </div>
                    )}

                    <div className="mt-8 pt-4 border-t border-white/5 flex justify-between items-center text-[9px] font-mono text-slate-600">
                        <span>Klyndu</span>
                        <span>Datos cifrados</span>
                    </div>
                </div>
            </div>
        </LoginBackground>
    );
}